import {
	useCallback,
	useEffect,
	useLayoutEffect,
	useMemo,
	useRef,
	useState,
} from 'react';
import { AnimatePresence, motion } from 'framer-motion';
import { Check } from 'lucide-react';
import { useGetSessionPosProcessingStatus } from '@/services';
import { PRONTUARIO_BG_CLASS } from '@/pages/professional/sessao-chamada/constants/prontuarioTheme';
import { SessionCallProntuarioAudioVisualizer } from '@/pages/professional/sessao-chamada/components/prontuario/SessionCallProntuarioAudioVisualizer';
import { cn } from '@/lib/utils';
import { TransitionCameraIcon } from './TransitionCameraIcon';
import {
	TransitionPaperSheet,
	LINE_INDENT_PCT,
	LINE_STEP_PX,
	PAPER_HEADER_PX,
	PAPER_PAD_PX,
	type PaperLine,
	type PaperWord,
} from './TransitionPaperSheet';
import {
	TransitionTextBalloon,
	type BalloonPoint,
	type BalloonVariant,
} from './TransitionTextBalloon';
import { SessionEndTransitionProgressBar } from './SessionEndTransitionProgressBar';
import type { SessionEndTransitionProgressBarHandle } from './SessionEndTransitionProgressBar';
import {
	resolveSessionEndTransitionTimeProgress,
	rushSessionEndTransitionProgress,
	SESSION_END_SAFETY_TIMEOUT_MS,
} from './sessionEndTransitionProgress';

const POLL_START_DELAY_MS = 10_000;
const SAFETY_TIMEOUT_MS = SESSION_END_SAFETY_TIMEOUT_MS;
const EMIT_INTERVAL_MS = 1_000;
const BALLOON_FLIGHT_MS = 1_500;
// Emissão alternada: pico de 3 mensagens só a cada 5s; nos demais, poucas.
const BALLOONS_PEAK = 3;
const BALLOON_PEAK_EVERY = 5;
const BALLOON_BASE_CHANCE = 0.88;

// Motor de "digitação": cada mensagem acrescenta uma pequena "palavra"
// (retângulo) à linha atual; várias palavras preenchem a linha antes de quebrar.
const BASE_WORD_MS = 300; // escrita base (lenta) de uma palavra
const MIN_WORD_MS = 130; // escrita mínima quando o backlog está alto
const BASE_GAP_MS = 120; // pausa entre palavras
const MIN_GAP_MS = 20;
const BACKLOG_SOFT = 6; // backlog em que a escrita atinge a velocidade máxima
const MAX_LINES = 80; // teto de DOM (a folha "rola" — efetivamente infinita)

// Dimensões aleatórias das palavras e da "lotação" de cada linha (% da linha).
const WORD_MIN_PCT = 9;
const WORD_MAX_PCT = 40;
const WORD_GAP_PCT = 2;
const LINE_FULL_MIN = 50;
const LINE_FULL_MAX = 88;

const resolveLineCapacity = (indent: boolean) =>
	100 - (indent ? LINE_INDENT_PCT : 0);

// Intro encadeada (vídeo → prontuário → carregando → animação).
const INTRO_FADE_MS = 380;
const INTRO_CHROME_HOLD_MS = 560;
const INTRO_CHROME_EXIT_MS = 440;
const INTRO_MORPH_MS = 540;

// Sequência de finalização (a partir de is_done).
const CAMERA_EXIT_MS = 550;
const PAPER_MOVE_DELAY_MS = 300; // espera após a câmera sumir
const PAPER_MOVE_MS = 650;
const SCAN_MS = 2_600;
const HIGHLIGHT_RATIO = 0.55; // fração das linhas que o scanner ilumina
const ABSORB_MS = 1_250; // folha encolhe e "entra" na pílula de loading
const PILL_READY_HOLD_MS = 1_050; // tempo mostrando "Prontuário pronto"

const CAMERA_SIZE = 72;
const CAMERA_HALF = CAMERA_SIZE / 2;
const PAPER_W = 210;
const PAPER_H = 290;
const PAPER_HALF_W = PAPER_W / 2;
const PAPER_HALF_H = PAPER_H / 2;

// Geometria do corpo rolável (deve casar com TransitionPaperSheet).
const PAPER_BODY_H = PAPER_H - 2 * PAPER_PAD_PX - PAPER_HEADER_PX;
const PAPER_BODY_USABLE_H = PAPER_BODY_H - LINE_STEP_PX; // 1 linha de respiro

type Phase = 'intro' | 'streaming' | 'finishing' | 'scanning' | 'absorbing';
type AreaSize = { w: number; h: number };
type InFlightBalloon = {
	id: number;
	from: BalloonPoint;
	to: BalloonPoint;
	durationMs: number;
	variant: BalloonVariant;
};
type Splash = { id: number; x: number; y: number; variant: BalloonVariant };

const randomBetween = (min: number, max: number) =>
	min + Math.random() * (max - min);

const clamp = (value: number, min: number, max: number) =>
	Math.min(max, Math.max(min, value));

const lerp = (from: number, to: number, t: number) => from + (to - from) * t;

const measureTransitionAreaSize = (
	node: HTMLDivElement,
	embedded: boolean,
): AreaSize => {
	if (embedded) {
		return { w: node.clientWidth, h: node.clientHeight };
	}

	const rect = node.getBoundingClientRect();
	return { w: rect.width, h: rect.height };
};

const measureCenterInTransitionArea = (
	target: HTMLElement,
	area: HTMLDivElement,
	embedded: boolean,
): BalloonPoint => {
	const areaRect = area.getBoundingClientRect();
	const targetRect = target.getBoundingClientRect();
	const offsetX = targetRect.left + targetRect.width / 2 - areaRect.left;
	const offsetY = targetRect.top + targetRect.height / 2 - areaRect.top;

	if (!embedded) {
		return { x: offsetX, y: offsetY };
	}

	const scaleX = area.clientWidth / areaRect.width || 1;
	const scaleY = area.clientHeight / areaRect.height || 1;

	return {
		x: offsetX * scaleX,
		y: offsetY * scaleY,
	};
};

const cameraPoint = (area: AreaSize, embedded: boolean): BalloonPoint => ({
	x: area.w * (embedded ? 0.4 : 0.34),
	y: area.h * 0.5,
});

const paperPoint = (
	area: AreaSize,
	centered: boolean,
	embedded: boolean,
): BalloonPoint => ({
	x: centered ? area.w * 0.5 : area.w * (embedded ? 0.62 : 0.6),
	y: area.h * 0.5,
});

const buildWord = (id: number, growMs: number): PaperWord => ({
	id,
	widthPct: randomBetween(WORD_MIN_PCT, WORD_MAX_PCT),
	growMs,
});

/** Peso variável: a maioria das mensagens insere 1 palavra, algumas 2. */
const balloonWeight = () => {
	return 1;
	// const roll = Math.random();
	// if (roll < 0.28) return 1;
	// if (roll < 0.82) return 2;
	// return 3;
};

const SPLASH_COLOR: Record<BalloonVariant, string> = {
	professional: 'rgba(138,180,248,0.9)',
	patient: 'rgba(184,156,246,0.9)',
};

type SessionEndTransitionProps = {
	sessionId: string;
	/** Chamado uma única vez ao final (sucesso, erro ou timeout). */
	onComplete: () => void;
	/** Chamado no momento exato em que o pós-processamento da IA é marcado como concluído. */
	onProcessingDone?: () => void;
	/** Demo/tutorial: simula is_done após N ms do início da animação (sem polling). */
	demoSimulateProcessingDoneMs?: number;
	/** Embutido em container fixo (ex.: landing demo) — layout relativo e composição centralizada. */
	embedded?: boolean;
};

/**
 * Animação de transição entre o fim da chamada P2P e o prontuário gerado por IA.
 * Faz um fadeout da chamada, "expande" o painel de prontuário (cuja pílula de
 * áudio vira a pílula de carregamento) e cobre o pós-processamento com a
 * metáfora câmera → balões → folha escrita por IA. Ao terminar, o scanner azul
 * varre a folha, que então encolhe e "entra" na pílula — que vira "Prontuário
 * pronto" com um check — antes de navegar para a dashboard.
 */
const SessionEndTransition = ({
	sessionId,
	onComplete,
	onProcessingDone,
	demoSimulateProcessingDoneMs,
	embedded = false,
}: SessionEndTransitionProps) => {
	const prefersReducedMotion = useMemo(
		() =>
			typeof window !== 'undefined' &&
			window.matchMedia('(prefers-reduced-motion: reduce)').matches,
		[],
	);

	const areaRef = useRef<HTMLDivElement>(null);
	const pillRef = useRef<HTMLDivElement>(null);
	const [area, setArea] = useState<AreaSize>({ w: 0, h: 0 });
	const [introStage, setIntroStage] = useState(0); // 0 fade · 1 chrome · 2 morph · 3 pronto
	const [started, setStarted] = useState(false);
	const [phase, setPhase] = useState<Phase>('intro');
	const [lines, setLines] = useState<PaperLine[]>([]);
	const [balloons, setBalloons] = useState<InFlightBalloon[]>([]);
	const [splashes, setSplashes] = useState<Splash[]>([]);
	const [pollingEnabled, setPollingEnabled] = useState(false);
	const [paperCentered, setPaperCentered] = useState(false);
	const [scanProgress, setScanProgress] = useState<number | null>(null);
	const [pillTarget, setPillTarget] = useState<BalloonPoint | null>(null);
	const [pillReady, setPillReady] = useState(false);

	// Refs espelho para uso dentro de timers/closures sem dependências obsoletas.
	const areaSizeRef = useRef<AreaSize>(area);
	areaSizeRef.current = area;
	const phaseRef = useRef<Phase>(phase);
	phaseRef.current = phase;
	const elapsedSecRef = useRef(0);
	const lineIdRef = useRef(0);
	const wordIdRef = useRef(0);
	const linesCountRef = useRef(0);
	const currentLineWidthRef = useRef(0); // % já preenchida da linha atual
	const currentLineIndentRef = useRef(false);
	const lineTargetRef = useRef(randomBetween(LINE_FULL_MIN, LINE_FULL_MAX));
	const balloonIdRef = useRef(0);
	const splashIdRef = useRef(0);
	const hasCompletedRef = useRef(false);
	const pendingRef = useRef(0); // linhas na fila para serem escritas
	const writerActiveRef = useRef(false);
	const writeTimeoutRef = useRef<ReturnType<typeof setTimeout>>();
	const emitIntervalRef = useRef<ReturnType<typeof setInterval>>();
	const scanIntervalRef = useRef<ReturnType<typeof setInterval>>();
	const reducedFeedRef = useRef<ReturnType<typeof setInterval>>();
	const balloonTimeoutsRef = useRef<ReturnType<typeof setTimeout>[]>([]);
	const finishTimeoutsRef = useRef<ReturnType<typeof setTimeout>[]>([]);
	const hasProcessingDoneRef = useRef(false);
	const progressBarRef = useRef<SessionEndTransitionProgressBarHandle>(null);
	const progressRef = useRef(0);
	const rushToCompleteRef = useRef(false);
	const finishingTriggeredRef = useRef(false);
	const streamingStartedAtRef = useRef<number | null>(null);

	const { data, isError } = useGetSessionPosProcessingStatus(sessionId, {
		enabled: pollingEnabled,
		retry: false,
	});

	const complete = useCallback(() => {
		if (hasCompletedRef.current) return;
		hasCompletedRef.current = true;
		onComplete();
	}, [onComplete]);

	// Mede a área de animação (posiciona câmera, papel e balões em px).
	useLayoutEffect(() => {
		const node = areaRef.current;
		if (!node) return;

		const sync = () => {
			setArea(measureTransitionAreaSize(node, embedded));
		};
		sync();

		const observer = new ResizeObserver(sync);
		observer.observe(node);
		return () => observer.disconnect();
	}, [embedded]);

	// Motor de digitação: escreve uma "palavra" por vez, acelerando com o backlog.
	// As palavras se acumulam na linha atual até enchê-la, então quebra a linha.
	const writeNextWord = useCallback(() => {
		if (phaseRef.current !== 'streaming') {
			writerActiveRef.current = false;
			return;
		}
		if (pendingRef.current <= 0 || linesCountRef.current >= MAX_LINES) {
			writerActiveRef.current = false;
			return;
		}

		pendingRef.current -= 1;
		const backlog = pendingRef.current;
		const t = clamp(backlog / BACKLOG_SOFT, 0, 1);
		const growMs = lerp(BASE_WORD_MS, MIN_WORD_MS, t);
		const gapMs = lerp(BASE_GAP_MS, MIN_GAP_MS, t);

		const word = buildWord(wordIdRef.current++, growMs);
		const lineCapacity = resolveLineCapacity(currentLineIndentRef.current);
		const gapBeforeWord =
			currentLineWidthRef.current > 0 ? WORD_GAP_PCT : 0;
		const remainingOnLine =
			lineCapacity - currentLineWidthRef.current - gapBeforeWord;
		const needNewLine =
			currentLineWidthRef.current === 0 ||
			remainingOnLine < WORD_MIN_PCT ||
			currentLineWidthRef.current + gapBeforeWord + word.widthPct >
				lineTargetRef.current;

		if (needNewLine) {
			const indent = Math.random() < 0.18;
			const capacity = resolveLineCapacity(indent);
			currentLineIndentRef.current = indent;
			lineTargetRef.current = Math.min(
				randomBetween(LINE_FULL_MIN, LINE_FULL_MAX),
				capacity,
			);
			word.widthPct = clamp(word.widthPct, WORD_MIN_PCT, capacity);
			const line: PaperLine = {
				id: lineIdRef.current++,
				words: [word],
				indent,
				isHighlight: false,
			};
			setLines((prev) => {
				const next = [...prev, line];
				linesCountRef.current = next.length;
				return next;
			});
			currentLineWidthRef.current = word.widthPct;
		} else {
			word.widthPct = clamp(
				word.widthPct,
				WORD_MIN_PCT,
				Math.max(WORD_MIN_PCT, remainingOnLine),
			);
			currentLineWidthRef.current += word.widthPct + WORD_GAP_PCT;
			setLines((prev) => {
				if (prev.length === 0) return prev;
				const last = prev[prev.length - 1];
				const updated = { ...last, words: [...last.words, word] };
				return [...prev.slice(0, -1), updated];
			});
		}

		writeTimeoutRef.current = setTimeout(writeNextWord, growMs + gapMs);
	}, []);

	const kickWriter = useCallback(() => {
		if (writerActiveRef.current) return;
		writerActiveRef.current = true;
		writeNextWord();
	}, [writeNextWord]);

	const enqueueWords = useCallback(
		(count: number) => {
			pendingRef.current = Math.min(
				pendingRef.current + count,
				MAX_LINES * 8,
			);
			kickWriter();
		},
		[kickWriter],
	);

	const spawnSplash = useCallback(
		(point: BalloonPoint, variant: BalloonVariant) => {
			const id = splashIdRef.current++;
			setSplashes((prev) => [
				...prev,
				{ id, x: point.x, y: point.y, variant },
			]);
		},
		[],
	);

	const handleBalloonArrive = useCallback(
		(id: number) => {
			setBalloons((prev) => {
				const balloon = prev.find((item) => item.id === id);
				if (balloon) spawnSplash(balloon.to, balloon.variant);
				return prev.filter((item) => item.id !== id);
			});
			enqueueWords(balloonWeight());
		},
		[enqueueWords, spawnSplash],
	);

	const emitBalloons = useCallback(() => {
		const currentArea = areaSizeRef.current;
		if (currentArea.w === 0) return;

		const sec = elapsedSecRef.current;
		const count =
			sec > 0 && sec % BALLOON_PEAK_EVERY === 0
				? BALLOONS_PEAK
				: Math.random() < BALLOON_BASE_CHANCE
				  ? 1
				  : 0;
		if (count === 0) return;

		const from = cameraPoint(currentArea, embedded);
		const paper = paperPoint(currentArea, false, embedded);

		for (let i = 0; i < count; i += 1) {
			const delay = randomBetween(0, EMIT_INTERVAL_MS * 0.7);
			const timeoutId = setTimeout(() => {
				if (phaseRef.current !== 'streaming') return;
				const id = balloonIdRef.current++;
				const to: BalloonPoint = {
					x:
						paper.x +
						randomBetween(-PAPER_HALF_W * 0.5, PAPER_HALF_W * 0.5),
					y:
						paper.y +
						randomBetween(-PAPER_HALF_H * 0.6, PAPER_HALF_H * 0.6),
				};
				setBalloons((prev) => [
					...prev,
					{
						id,
						from,
						to,
						durationMs:
							BALLOON_FLIGHT_MS * randomBetween(0.92, 1.12),
						variant: id % 2 === 0 ? 'professional' : 'patient',
					},
				]);
			}, delay);
			balloonTimeoutsRef.current.push(timeoutId);
		}
	}, [embedded]);

	// Folha encolhe e "entra" na pílula de loading; depois a pílula vira "pronto".
	const startAbsorbing = useCallback(() => {
		const pillEl = pillRef.current;
		const areaEl = areaRef.current;
		if (pillEl && areaEl) {
			setPillTarget(
				measureCenterInTransitionArea(pillEl, areaEl, embedded),
			);
		}

		phaseRef.current = 'absorbing';
		setPhase('absorbing');
		setScanProgress(null);

		const readyTimer = setTimeout(
			() => setPillReady(true),
			Math.round(ABSORB_MS * 0.74),
		);
		const doneTimer = setTimeout(complete, ABSORB_MS + PILL_READY_HOLD_MS);
		finishTimeoutsRef.current.push(readyTimer, doneTimer);
	}, [complete, embedded]);

	const startScanning = useCallback(() => {
		phaseRef.current = 'scanning';
		setPhase('scanning');

		const count = Math.max(1, linesCountRef.current);
		// Apenas algumas linhas (~55%) acendem em azul, como um scanner real.
		const candidates = new Set<number>();
		for (let i = 0; i < count; i += 1) {
			if (Math.random() < HIGHLIGHT_RATIO) candidates.add(i);
		}
		const finalScroll = Math.max(
			0,
			count * LINE_STEP_PX - PAPER_BODY_USABLE_H,
		);

		const steps = Math.max(14, count);
		const step = SCAN_MS / steps;
		let idx = 0;
		setScanProgress(0);
		scanIntervalRef.current = setInterval(() => {
			idx += 1;
			const progress = idx / steps;
			setScanProgress(progress);
			// Acende a linha exatamente quando o feixe (px) passa por ela.
			const barY = progress * PAPER_BODY_H;
			setLines((current) =>
				current.map((line, i) =>
					candidates.has(i) && i * LINE_STEP_PX - finalScroll <= barY
						? { ...line, isHighlight: true }
						: line,
				),
			);
			if (idx >= steps) {
				if (scanIntervalRef.current)
					clearInterval(scanIntervalRef.current);
				startAbsorbing();
			}
		}, step);
	}, [startAbsorbing]);

	const startFinishing = useCallback(() => {
		if (phaseRef.current !== 'streaming') return;
		phaseRef.current = 'finishing';
		setPhase('finishing');

		// Para a emissão e a escrita — a folha finaliza com o que já tem (item 5).
		if (emitIntervalRef.current) clearInterval(emitIntervalRef.current);
		if (writeTimeoutRef.current) clearTimeout(writeTimeoutRef.current);
		writerActiveRef.current = false;
		pendingRef.current = 0;
		setBalloons([]);

		// A folha só começa a se mover 300ms depois que a câmera some.
		const moveTimer = setTimeout(
			() => setPaperCentered(true),
			CAMERA_EXIT_MS + PAPER_MOVE_DELAY_MS,
		);
		// Depois que a folha assenta, o scanner azul passa por cima.
		const scanTimer = setTimeout(
			startScanning,
			CAMERA_EXIT_MS + PAPER_MOVE_DELAY_MS + PAPER_MOVE_MS + 150,
		);
		finishTimeoutsRef.current.push(moveTimer, scanTimer);
	}, [startScanning]);

	// Barra de progresso: 0→80% em 40s, 80→100% em 30min; ao receber is_done, corre até 100%.
	useEffect(() => {
		if (!started || phase !== 'streaming') return;

		streamingStartedAtRef.current = performance.now();
		let raf = 0;

		const tick = () => {
			if (phaseRef.current !== 'streaming') return;

			const elapsed =
				performance.now() -
				(streamingStartedAtRef.current ?? performance.now());
			const next = rushToCompleteRef.current
				? rushSessionEndTransitionProgress(progressRef.current)
				: resolveSessionEndTransitionTimeProgress(elapsed);

			progressRef.current = next;
			progressBarRef.current?.setProgress(next);

			if (
				rushToCompleteRef.current &&
				next >= 1 &&
				hasProcessingDoneRef.current &&
				!finishingTriggeredRef.current
			) {
				finishingTriggeredRef.current = true;
				startFinishing();
				return;
			}

			raf = requestAnimationFrame(tick);
		};

		raf = requestAnimationFrame(tick);
		return () => cancelAnimationFrame(raf);
	}, [phase, started, startFinishing]);

	// Inicia polling após 10s; timeout de segurança alinhado à fase lenta (≈31 min).
	useEffect(() => {
		if (demoSimulateProcessingDoneMs != null) return;

		const pollTimer = setTimeout(
			() => setPollingEnabled(true),
			POLL_START_DELAY_MS,
		);
		const safetyTimer = setTimeout(complete, SAFETY_TIMEOUT_MS);
		return () => {
			clearTimeout(pollTimer);
			clearTimeout(safetyTimer);
		};
	}, [complete, demoSimulateProcessingDoneMs]);

	// Demo/tutorial: conclui o pós-processamento sem polling real.
	useEffect(() => {
		if (demoSimulateProcessingDoneMs == null || !started) return;
		if (hasProcessingDoneRef.current) return;

		const timer = setTimeout(() => {
			if (hasProcessingDoneRef.current) return;
			hasProcessingDoneRef.current = true;
			onProcessingDone?.();
			rushToCompleteRef.current = true;
		}, demoSimulateProcessingDoneMs);

		return () => clearTimeout(timer);
	}, [demoSimulateProcessingDoneMs, onProcessingDone, started]);

	// Intro encadeada: fadeout do vídeo → chrome do prontuário some → áudio vira
	// "carregando" → câmera/folha entram e a emissão começa.
	useEffect(() => {
		const t1 = setTimeout(() => setIntroStage(1), INTRO_FADE_MS);
		const t2 = setTimeout(
			() => setIntroStage(2),
			INTRO_FADE_MS + INTRO_CHROME_HOLD_MS,
		);
		const introEnd =
			INTRO_FADE_MS +
			INTRO_CHROME_HOLD_MS +
			Math.max(INTRO_CHROME_EXIT_MS, INTRO_MORPH_MS);
		const t3 = setTimeout(() => {
			setIntroStage(3);
			setStarted(true);
			phaseRef.current = 'streaming';
			setPhase('streaming');

			if (prefersReducedMotion) {
				reducedFeedRef.current = setInterval(() => {
					if (phaseRef.current !== 'streaming') return;
					enqueueWords(1);
				}, 650);
				return;
			}

			emitIntervalRef.current = setInterval(() => {
				if (phaseRef.current !== 'streaming') return;
				elapsedSecRef.current += 1;
				emitBalloons();
			}, EMIT_INTERVAL_MS);
		}, introEnd);

		return () => {
			clearTimeout(t1);
			clearTimeout(t2);
			clearTimeout(t3);
		};
	}, [emitBalloons, enqueueWords, prefersReducedMotion]);

	// Espelha is_done, pré-busca o prontuário e acelera a barra até 100% antes do final.
	useEffect(() => {
		if (demoSimulateProcessingDoneMs != null) return;
		if (!data?.is_done || hasProcessingDoneRef.current) return;

		hasProcessingDoneRef.current = true;
		console.log(
			'[prefetch-prontuario] is_done=true — triggering prefetch',
			{
				sessionId,
				posProcessingData: data,
			},
		);
		onProcessingDone?.();
		rushToCompleteRef.current = true;
	}, [
		data?.is_done,
		demoSimulateProcessingDoneMs,
		onProcessingDone,
		sessionId,
		data,
	]);

	// Erro de servidor/rede → vai direto para a dashboard.
	useEffect(() => {
		if (isError) complete();
	}, [isError, complete]);

	// Limpeza geral de timers ao desmontar.
	useEffect(() => {
		const balloonTimeouts = balloonTimeoutsRef.current;
		const finishTimeouts = finishTimeoutsRef.current;
		return () => {
			if (emitIntervalRef.current) clearInterval(emitIntervalRef.current);
			if (scanIntervalRef.current) clearInterval(scanIntervalRef.current);
			if (reducedFeedRef.current) clearInterval(reducedFeedRef.current);
			if (writeTimeoutRef.current) clearTimeout(writeTimeoutRef.current);
			balloonTimeouts.forEach(clearTimeout);
			finishTimeouts.forEach(clearTimeout);
		};
	}, []);

	const isLeaving =
		phase === 'finishing' || phase === 'scanning' || phase === 'absorbing';
	const isAbsorbing = phase === 'absorbing';
	const centered = prefersReducedMotion || paperCentered;
	const camTarget = cameraPoint(area, embedded);
	const paperTarget = paperPoint(area, centered, embedded);
	const paperCenter = paperPoint(area, true, embedded);

	// Rolagem: quando o conteúdo ultrapassa a área útil, sobe mantendo respiro.
	const contentH = lines.length * LINE_STEP_PX;
	const scrollPx = Math.max(0, contentH - PAPER_BODY_USABLE_H);
	const thumb =
		scrollPx > 0
			? { heightPct: clamp(PAPER_BODY_USABLE_H / contentH, 0.14, 1) }
			: null;
	const scanTopPx =
		phase === 'scanning' && scanProgress != null
			? scanProgress * PAPER_BODY_H
			: null;

	const absorbTo = pillTarget ?? paperCenter;
	const progressBarTop = area.h > 0 ? area.h * 0.5 + PAPER_HALF_H + 36 : 0;
	const showProgressBar = started && phase === 'streaming' && area.h > 0;

	const paperTransition = isAbsorbing
		? {
				duration: ABSORB_MS / 1000,
				ease: [0.55, 0, 0.85, 0.3] as [number, number, number, number],
				times: [0, 0.45, 1],
		  }
		: paperCentered
		  ? {
					duration: PAPER_MOVE_MS / 1000,
					ease: [0.4, 0, 0.2, 1] as [number, number, number, number],
		    }
		  : {
					duration: 0.7,
					ease: [0.34, 1.3, 0.64, 1] as [
						number,
						number,
						number,
						number,
					],
		    };

	const pillMode = pillReady ? 'ready' : introStage < 2 ? 'audio' : 'loading';

	return (
		<motion.div
			className={cn(
				embedded
					? 'relative flex size-full flex-col overflow-hidden'
					: 'absolute inset-0 z-50 flex flex-col overflow-hidden',
				PRONTUARIO_BG_CLASS,
			)}
			initial={{ opacity: 0 }}
			animate={{ opacity: 1 }}
			transition={{ duration: INTRO_FADE_MS / 1000, ease: 'easeOut' }}
		>
			{/* Pílula superior: container de áudio → carregando → prontuário pronto. */}
			<div className="relative flex justify-center pt-6">
				{/* Flash/feixe de luz quando a folha entra na pílula. */}
				<AnimatePresence>
					{pillReady && (
						<motion.span
							key="pill-flash"
							className="pointer-events-none absolute top-1 h-12 w-48 rounded-full bg-[#8ab4f8]/40 blur-2xl"
							initial={{ opacity: 0, scale: 0.4 }}
							animate={{
								opacity: [0, 0.9, 0],
								scale: [0.4, 1.3, 1.1],
							}}
							transition={{ duration: 0.9, ease: 'easeOut' }}
						/>
					)}
				</AnimatePresence>

				<motion.div
					ref={pillRef}
					layout
					className={cn(
						'relative inline-flex items-center justify-center gap-2 rounded-full px-4 py-1.5 ring-1 transition-colors duration-500',
						pillReady
							? 'bg-[#8ab4f8]/10 ring-[#8ab4f8]'
							: 'bg-white/[0.06] ring-white/10',
					)}
					transition={{ duration: 0.45, ease: [0.4, 0, 0.2, 1] }}
				>
					<AnimatePresence mode="wait" initial={false}>
						{pillMode === 'audio' ? (
							<motion.div
								key="audio"
								initial={{ opacity: 0 }}
								animate={{ opacity: 1 }}
								exit={{ opacity: 0 }}
								transition={{ duration: 0.25 }}
							>
								<SessionCallProntuarioAudioVisualizer
									size="large"
									decorative
								/>
							</motion.div>
						) : pillMode === 'loading' ? (
							<motion.div
								key="loading"
								className="flex items-center gap-2 text-[13px] font-medium text-slate-300"
								initial={{ opacity: 0 }}
								animate={{ opacity: 1 }}
								exit={{ opacity: 0 }}
								transition={{ duration: INTRO_MORPH_MS / 1000 }}
							>
								<span className="size-2 rounded-full bg-[#8ab4f8] motion-safe:animate-pulse" />
								<span className="animate-pulse">
									Gerando prontuário com IA…
								</span>
							</motion.div>
						) : (
							<motion.div
								key="ready"
								className="flex items-center gap-2 text-[13px] font-semibold text-[#cfe0ff]"
								initial={{ opacity: 0 }}
								animate={{ opacity: 1 }}
								transition={{ duration: 0.3 }}
							>
								<motion.span
									className="flex size-4 items-center justify-center rounded-full bg-[#8ab4f8] text-[#0f1722]"
									initial={{ scale: 0 }}
									animate={{ scale: [0, 1.35, 1] }}
									transition={{
										duration: 0.5,
										ease: 'easeOut',
										times: [0, 0.6, 1],
									}}
								>
									<Check size={11} strokeWidth={3.5} />
								</motion.span>
								<span>Prontuário pronto</span>
							</motion.div>
						)}
					</AnimatePresence>
				</motion.div>
			</div>

			<div ref={areaRef} className="relative min-h-0 flex-1">
				{/* Chrome "fantasma" do prontuário que aparece e some no intro. */}
				<AnimatePresence>
					{introStage === 1 && (
						<motion.div
							key="chrome"
							className="pointer-events-none absolute inset-x-0 top-0 mx-auto flex max-w-[420px] flex-col gap-4 px-6 pt-4"
							initial={{ opacity: 0, y: 8 }}
							animate={{ opacity: 1, y: 0 }}
							exit={{ opacity: 0, y: -10 }}
							transition={{
								duration: INTRO_CHROME_EXIT_MS / 1000,
								ease: 'easeInOut',
							}}
						>
							<div className="flex items-center justify-between">
								<span className="h-7 w-20 rounded-full bg-white/[0.08]" />
								<span className="size-7 rounded-full bg-white/[0.08]" />
							</div>
							<div className="flex items-center gap-3 rounded-xl border border-white/[0.08] bg-[#1a2430] p-3">
								<span className="size-9 rounded-full bg-white/[0.08]" />
								<div className="flex flex-1 flex-col gap-2">
									<span className="h-3 w-1/2 rounded-full bg-white/[0.1]" />
									<span className="h-2.5 w-1/3 rounded-full bg-white/[0.06]" />
								</div>
							</div>
							<div className="flex flex-col gap-2">
								<span className="h-2.5 w-full rounded-full bg-white/[0.06]" />
								<span className="h-2.5 w-[88%] rounded-full bg-white/[0.06]" />
								<span className="h-2.5 w-2/3 rounded-full bg-white/[0.06]" />
							</div>
						</motion.div>
					)}
				</AnimatePresence>

				{area.w > 0 && started && (
					<>
						{!prefersReducedMotion && (
							<motion.div
								className="absolute left-0 top-0"
							initial={{
								x:
									(embedded
										? camTarget.x
										: area.w * 0.5) - CAMERA_HALF,
								y: camTarget.y - CAMERA_HALF,
								opacity: 0,
								scale: 0.8,
							}}
								animate={
									isLeaving
										? {
												x: camTarget.x - CAMERA_HALF,
												y: camTarget.y - CAMERA_HALF,
												opacity: [1, 1, 0],
												scale: [1, 1.18, 0],
										  }
										: {
												x: camTarget.x - CAMERA_HALF,
												y: camTarget.y - CAMERA_HALF,
												opacity: 1,
												scale: 1,
										  }
								}
								transition={
									isLeaving
										? {
												duration: CAMERA_EXIT_MS / 1000,
												ease: 'easeInOut',
												times: [0, 0.4, 1],
										  }
										: { duration: 0.6, ease: 'easeOut' }
								}
							>
								<TransitionCameraIcon />
							</motion.div>
						)}

						<AnimatePresence>
							{balloons.map((balloon) => (
								<TransitionTextBalloon
									key={balloon.id}
									id={balloon.id}
									from={balloon.from}
									to={balloon.to}
									durationMs={balloon.durationMs}
									variant={balloon.variant}
									onArrive={handleBalloonArrive}
								/>
							))}
						</AnimatePresence>

						{/* Feedback de chegada na folha. */}
						<AnimatePresence>
							{splashes.map((splash) => (
								<motion.span
									key={splash.id}
									className="pointer-events-none absolute left-0 top-0 rounded-full"
									style={{
										width: 40,
										height: 40,
										x: splash.x - 20,
										y: splash.y - 20,
										border: `2px solid ${
											SPLASH_COLOR[splash.variant]
										}`,
									}}
									initial={{ opacity: 0.85, scale: 0.2 }}
									animate={{ opacity: 0, scale: 2.6 }}
									transition={{
										duration: 0.55,
										ease: 'easeOut',
									}}
									onAnimationComplete={() =>
										setSplashes((prev) =>
											prev.filter(
												(item) => item.id !== splash.id,
											),
										)
									}
								/>
							))}
						</AnimatePresence>

						{/* Feixe de luz vertical conectando a folha à pílula na absorção. */}
						<AnimatePresence>
							{isAbsorbing && (
								<motion.span
									key="beam"
									className="pointer-events-none absolute left-1/2 top-0 -translate-x-1/2 rounded-full bg-gradient-to-b from-[#8ab4f8]/80 via-[#8ab4f8]/25 to-transparent blur-md"
									style={{ width: 10, height: area.h * 0.55 }}
									initial={{ opacity: 0 }}
									animate={{ opacity: [0, 0.85, 0] }}
									transition={{
										duration: ABSORB_MS / 1000,
										ease: 'easeInOut',
									}}
								/>
							)}
						</AnimatePresence>

						<motion.div
							className="absolute left-0 top-0"
							style={{ width: PAPER_W, height: PAPER_H }}
							initial={{
								x: paperPoint(area, false, embedded).x - PAPER_HALF_W,
								y: camTarget.y - PAPER_HALF_H,
								opacity: 0,
								scale: 0.35,
							}}
							animate={
								isAbsorbing
									? {
											x: [
												paperCenter.x - PAPER_HALF_W,
												paperCenter.x - PAPER_HALF_W,
												absorbTo.x - PAPER_HALF_W,
											],
											y: [
												paperCenter.y - PAPER_HALF_H,
												paperCenter.y - PAPER_HALF_H,
												absorbTo.y - PAPER_HALF_H,
											],
											opacity: [1, 1, 0],
											scale: [1.03, 0.2, 0.05],
									  }
									: {
											x: paperTarget.x - PAPER_HALF_W,
											y: paperTarget.y - PAPER_HALF_H,
											opacity: 1,
											scale:
												phase === 'scanning' ? 1.03 : 1,
									  }
							}
							transition={paperTransition}
						>
							<TransitionPaperSheet
								lines={lines}
								showCursor={phase === 'streaming'}
								scanTopPx={scanTopPx}
								scrollPx={scrollPx}
								thumb={thumb}
								className="h-full w-full"
							/>
						</motion.div>
					</>
				)}

				<SessionEndTransitionProgressBar
					ref={progressBarRef}
					visible={showProgressBar}
					top={progressBarTop}
				/>
			</div>
		</motion.div>
	);
};

export { SessionEndTransition };
